import { applyContextualSuggestionLayer } from '@/lib/assistantFreeText';
import { pickVariant, AB_NUDGE_DEFAULT_COPY, AB_CLOSING_COPY } from '@/lib/assistantABTesting';

function resolveVisibleProductsFromIds(menuData, ids) {
  const products = menuData?.products || [];
  const map = new Map(products.map((p) => [String(p.id), p]));
  const out = [];
  for (const id of ids || []) {
    const p = map.get(String(id));
    if (p && p.availability !== 'hidden') out.push(p);
  }
  return out;
}

function dedupeKeys(keys) {
  const seen = new Set();
  const out = [];
  for (const k of keys || []) {
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(k);
  }
  return out;
}

/**
 * Mensaje de nudge silencioso según memoria de sesión y productos vistos.
 *
 * @param {{
 *   sessionMemory: object,
 *   lastProducts: string[]|number[],
 *   lastIntent: string|null,
 *   menuData: object|null
 * }} ctx
 * @returns {{
 *   text: string,
 *   fallbackKeys: string[],
 *   anchorProducts?: object[],
 *   suggestedProducts?: object[],
 *   textAfterProducts?: string,
 *   abTracking?: { ab_test_key: string, ab_variant_id: string }
 * }}
 */
export function buildContextualNudge({ sessionMemory, lastProducts, lastIntent, menuData }) {
  const mem = sessionMemory || {};
  const intent = lastIntent != null ? lastIntent : mem.lastIntent;
  const lpRaw = lastProducts?.length ? lastProducts : mem.lastProducts || [];
  const flowCtx = {
    sessionMemory: { ...mem, lastProducts: lpRaw },
  };

  if (intent === 'order') {
    return {
      text: '¿Vamos cerrando el pedido o querés agregar algo más? 👇',
      fallbackKeys: dedupeKeys(['checkout_cart', 'catalog', 'main']),
    };
  }

  const resolved = resolveVisibleProductsFromIds(menuData, lpRaw);
  if (resolved.length && menuData) {
    let ans = {
      text: '¿Querés que te recomiende algo que combine con lo que viste? 👇',
      products: resolved.slice(0, 6),
      fallbackKeys: ['order_hint', 'catalog', 'popular_hint', 'main'],
    };
    ans = applyContextualSuggestionLayer(ans, menuData, flowCtx);
    const keys = dedupeKeys(ans.fallbackKeys?.length ? ans.fallbackKeys : ['order_hint', 'catalog', 'main']);
    const hasSug = ans.suggestedProducts?.length > 0;
    return {
      text: ans.text,
      fallbackKeys: keys,
      anchorProducts: ans.products?.length ? ans.products : undefined,
      suggestedProducts: hasSug ? ans.suggestedProducts : undefined,
      textAfterProducts: hasSug ? ans.textAfterProducts : undefined,
    };
  }

  if (Number(mem.partySize) >= 2) {
    return {
      text: '¿Te dejo algo bueno para compartir? 👇',
      fallbackKeys: dedupeKeys(['duo_hint', 'popular_hint', 'catalog', 'main']),
    };
  }

  if (mem.priceSensitivity === 'cheap') {
    return {
      text: '¿Querés ver algo más conveniente? 👇',
      fallbackKeys: dedupeKeys(['cheap_hint', 'catalog', 'main']),
    };
  }

  if (mem.servicePreference === 'delivery') {
    return {
      text: '¿Seguimos con tu pedido a domicilio o querés ver más opciones? 🚚',
      fallbackKeys: dedupeKeys(['order_delivery', 'catalog', 'main']),
    };
  }

  const v = pickVariant('nudge_default_copy', AB_NUDGE_DEFAULT_COPY);
  return {
    text: v.text,
    fallbackKeys: dedupeKeys(['popular_hint', 'categories_hint', 'main']),
    abTracking: { ab_test_key: 'nudge_default_copy', ab_variant_id: v.id },
  };
}
